import React, { useState } from "react";
import {
  Grid,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  Box,
  MenuItem,
  Button,
  Menu,
  Avatar,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  ArrowForward,
  CheckCircle,
  VerifiedUserOutlined,
  KeyboardArrowDown as KeyboardArrowDownIcon, // Added this
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { signUp } from "../../services/authenticationService";
import Toast from "../../component/toastify/Index";
import logo from "../../assets/icons/logo.svg";
import "../../styles/pages/auth/modernSignUp.scss";
import bgimg from "../../assets/images/ModernAuth.png";
import { SignUpInputData, languageDetail } from "../../content/content"; // Ensure languageDetail is imported

const ModernSignUp = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({ agb: false });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  // Language State logic from Navbar
  const [anchorLanguageEl, setAnchorLanguageEl] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(
    languageDetail.options[0],
  );
  const openLangMenu = Boolean(anchorLanguageEl);

  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    number: false,
    noSpaces: false,
  });

  const handleLanguageClick = (event) => {
    setAnchorLanguageEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorLanguageEl(null);
  };

  const handleLanguageSelect = (item) => {
    setSelectedLanguage(item);
    i18n.changeLanguage(item.value); // Sync with i18next
    setAnchorLanguageEl(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(e.target);
    const fieldValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: fieldValue }));

    if (name === "password") {
      setPasswordCriteria({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        number: /[0-9]/.test(value),
        noSpaces: value.length > 0 && !/\s/.test(value),
      });
    }
  };

  const validate = () => {
    let valid = true;
    const newErrors = {};

    SignUpInputData.forEach((item) => {
      if (!formData[item.name]?.trim()) {
        newErrors[item.name] = `${t(`${item.translationKey}`)} ${t(
          "validation.is_required",
        )}`;
        valid = false;
      } else if (
        item.name === "email" &&
        !/^\S+@\S+\.\S+$/.test(formData[item.name])
      ) {
        newErrors[item.name] = `${t("Bitte gültige E-Mail-Adresse eingeben")}`;
        valid = false;
      }
    });

    if (!formData.password) {
      newErrors.password = `${t("auth.password")} ${t(
        "validation.is_required",
      )}`;
      valid = false;
    } else if (
      !passwordCriteria.length ||
      !passwordCriteria.uppercase ||
      !passwordCriteria.number ||
      !passwordCriteria.noSpaces
    ) {
      newErrors.password = t("validation.meet_criteria");
      valid = false;
    }

    if (!formData.agb) {
      newErrors.agb = t("validation.agb_required");
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const response = await signUp(formData);
      if (response?.isSuccess) navigate(`/check-your-email/${formData.email}`);
    } catch (error) {
      Toast(error?.message, "error");
    }
  };

  return (
    <div className="modern-signup-page">
      <Grid container className="main-container">
        {/* LEFT SIDE: FORM */}
        <Grid item xs={12} md={5} className="form-section">
          <img src={logo} alt="Finanz Controller" className="logo" />
          <div className="headline-area">
            <h1>
              {t("marketing.headline")}{" "}
              <span className="highlight">{t("marketing.highlight")}</span>
            </h1>
            <div className="trust-badges">
              <span>
                <CheckCircle className="gold-icon" />{" "}
                {t("marketing.no_cancellation")}
              </span>
              <span>
                <CheckCircle className="gold-icon" />{" "}
                {t("marketing.no_hidden_costs")}
              </span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="signup-form">
            {SignUpInputData.map((field) => (
              <div key={field.name} className="input-group">
                <div className="label-row">
                  <label>{t(field.translationKey)}*</label>
                  {errors[field.name] && (
                    <span className="error-message">{errors[field.name]}</span>
                  )}
                </div>
                <div
                  className={`custom-input ${
                    errors[field.name] ? "error" : ""
                  }`}
                >
                  <span className="input-icon">{field.icon}</span>
                  <input
                    name={field.name}
                    placeholder={field.placeholder}
                    onChange={handleChange}
                  />
                </div>
              </div>
            ))}

            <div className="input-group">
              <div className="label-row">
                <label>{t("auth.password")}*</label>
                {errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
              </div>
              <div className={`custom-input ${errors.password ? "error" : ""}`}>
                <span className="input-icon">
                  <LockOutlined />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Passwort eingeben"
                  onChange={handleChange}
                />
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  className="visibility-toggle"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </div>
              <div className="password-checker-grid">
                <p className={passwordCriteria.length ? "valid" : ""}>
                  <CheckCircle /> {t("validation.min_chars")}
                </p>
                <p className={passwordCriteria.number ? "valid" : ""}>
                  <CheckCircle /> {t("validation.one_number")}
                </p>
                <p className={passwordCriteria.uppercase ? "valid" : ""}>
                  <CheckCircle /> {t("validation.one_uppercase")}
                </p>
                <p className={passwordCriteria.noSpaces ? "valid" : ""}>
                  <CheckCircle /> {t("validation.no_spaces")}
                </p>
              </div>
            </div>
            <div className="checkbox-group">
              <FormControlLabel
                className="terms-checkbox"
                control={
                  <Checkbox
                    name="agb"
                    checked={formData.agb || false}
                    onChange={handleChange}
                    sx={{
                      "&.Mui-checked": { color: "#B38E3F" },
                    }}
                  />
                }
                label={
                  <span>
                    {t("auth.accept_terms_1")}{" "}
                    <a href="https://finanz-controller.de/agb/">
                      {t("auth.accept_terms_agb")}
                    </a>{" "}
                    {t("auth.accept_terms_and")}{" "}
                    <a href="https://finanz-controller.de/datenschutz/">
                      {t("auth.accept_terms_privacy")}
                    </a>
                    .
                  </span>
                }
              />
              {errors.agb && (
                <span
                  className="error-message checkbox-error"
                  style={{
                    display: "block",
                    marginTop: "-10px",
                    marginBottom: "10px",
                  }}
                >
                  {errors.agb}
                </span>
              )}
            </div>
            <button type="submit" className="submit-btn">
              {t("auth.submit_btn")} <ArrowForward />
            </button>
            <p className="footer-note">
              <VerifiedUserOutlined /> {t("auth.footer_note")}
            </p>
          </form>
        </Grid>

        {/* RIGHT SIDE: MARKETING */}
        <Grid item xs={12} md={7} className="marketing-section">
          {/* UPDATED LANGUAGE SWITCHER */}
          <div className="lang-switcher">
            <Button
              variant="outlined"
              onClick={handleLanguageClick}
              sx={{
                textTransform: "none",
                border: "none",
                p: 0,
                "&:hover": { border: "none", background: "transparent" },
              }}
            >
              <Box display="flex" gap={1} alignItems="center">
                <IconButton
                  sx={{
                    p: 0,
                    "& .MuiAvatar-root": { height: "25px", width: "25px" },
                  }}
                >
                  <Avatar alt="selected-lang" src={selectedLanguage.image} />
                </IconButton>
                <Typography
                  className="navbar-font"
                  sx={{ color: "#333", fontWeight: 500 }}
                >
                  {selectedLanguage.label}
                </Typography>
                <KeyboardArrowDownIcon sx={{ color: "#B38E3F" }} />
              </Box>
            </Button>
            <Menu
              anchorEl={anchorLanguageEl}
              open={openLangMenu}
              onClose={handleLanguageClose}
              disableScrollLock={true}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              {languageDetail.options.map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={() => handleLanguageSelect(item)}
                  sx={{ py: 0.5 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      alt={item.label}
                      src={item.image}
                      sx={{ width: 20, height: 20 }}
                    />
                    <Typography className="navbar-font">
                      {item.label}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </div>

          <div className="marketing-content-container">
            <div className="marketing-text-wrapper">
              <h2>
                {t("marketing.main_title")}
                <br />
                <span className="gold-text">{t("marketing.sub_title")}</span>
              </h2>

              <ul className="benefit-list">
                <li>
                  <CheckCircle className="gold-icon" />
                  <Trans
                    i18nKey="marketing.benefit_1"
                    components={{ gold: <span className="gold" /> }}
                  />
                </li>
                <li>
                  <CheckCircle className="gold-icon" />
                  <Trans
                    i18nKey="marketing.benefit_2"
                    components={{ gold: <span className="gold" /> }}
                  />
                </li>
                <li>
                  <CheckCircle className="gold-icon" />
                  <Trans
                    i18nKey="marketing.benefit_3"
                    components={{ gold: <span className="gold" /> }}
                  />
                </li>
                <li>
                  <CheckCircle className="gold-icon" />
                  <Trans
                    i18nKey="marketing.benefit_4"
                    components={{ gold: <span className="gold" /> }}
                  />
                </li>
                <li>
                  <CheckCircle className="gold-icon" />
                  <Trans
                    i18nKey="marketing.benefit_5"
                    components={{ gold: <span className="gold" /> }}
                  />
                </li>
              </ul>
            </div>
            <div className="dashboard-preview-full">
              <img src={bgimg} alt="Dashboard Preview" />
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default ModernSignUp;
